import { RESUME_SYNC_QUEUE_KEY } from './resumeSync';
import { HISTORY_KEY } from './listeningHistory';
const ACTIVITY_QUEUE_KEY = 'rastyle.activity.queue.v1';
const RESUME_STATE_KEY = 'rastyle.activity.resume.v1';
const RESUME_STATE_CHANGED_EVENT = 'rastyle:activity-resume-changed';
const ANONYMOUS_ID_KEY = 'rastyle.activity.anonymous-id.v1';
const ACTIVITY_OWNER_KEY = 'rastyle.activity.member-id.v1';
const MAX_QUEUE_SIZE = 100;
const MAX_RESUME_ITEMS = 40;

const RESUME_KIND_TYPES = {
  listening: new Set(['mixtape', 'track', 'album', 'playlist', 'podcast', 'radio_show', 'audio']),
  watching: new Set(['video', 'video_mix', 'documentary', 'livestream_replay', 'replay']),
  reading: new Set(['article', 'interview', 'feature', 'story', 'document']),
};

const safeStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readJson = (key, fallback) => {
  const storage = safeStorage();
  if (!storage) return fallback;
  try {
    const value = JSON.parse(storage.getItem(key) || 'null');
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Activity tracking must never block the core experience.
  }
};

const clearMemberActivity = () => {
  const storage = safeStorage();
  if (!storage) return;
  [ACTIVITY_QUEUE_KEY, RESUME_STATE_KEY, ANONYMOUS_ID_KEY,
    RESUME_SYNC_QUEUE_KEY, HISTORY_KEY, ACTIVITY_OWNER_KEY].forEach((key) => storage.removeItem(key));
  try { window.dispatchEvent(new CustomEvent(RESUME_STATE_CHANGED_EVENT)); } catch { /* noop */ }
};

// A verified account switch cannot inherit the previous Member's browser state.
// First login may retain the current guest's progress for the explicit merge.
const prepareActivityIdentity = (memberId) => {
  if (!memberId) return;
  const storage = safeStorage();
  if (!storage) return;
  const previous = storage.getItem(ACTIVITY_OWNER_KEY);
  if (previous && previous !== String(memberId)) clearMemberActivity();
  storage.setItem(ACTIVITY_OWNER_KEY, String(memberId));
};

const createAnonymousId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getAnonymousId = () => {
  const storage = safeStorage();
  if (!storage) return 'anonymous';
  const existing = storage.getItem(ANONYMOUS_ID_KEY);
  if (existing) return existing;
  const id = createAnonymousId();
  try { storage.setItem(ANONYMOUS_ID_KEY, id); } catch { /* noop */ }
  return id;
};

const readActivityQueue = () => {
  const queue = readJson(ACTIVITY_QUEUE_KEY, []);
  return Array.isArray(queue) ? queue : [];
};

const queueActivity = ({ activity_type, entity, source = 'web', metadata = {} }) => {
  if (!activity_type) return readActivityQueue();
  const event = {
    event_id: createAnonymousId(),
    anonymous_id: getAnonymousId(),
    activity_type,
    entity_type: entity?.type || null,
    entity_id: entity?.id || null,
    source,
    metadata,
    occurred_at: new Date().toISOString(),
  };
  const next = [...readActivityQueue(), event].slice(-MAX_QUEUE_SIZE);
  writeJson(ACTIVITY_QUEUE_KEY, next);
  return next;
};

const flushActivityQueue = async ({ apiBase, token, fetchImpl = globalThis.fetch } = {}) => {
  const queue = readActivityQueue();
  if (!queue.length || !apiBase || typeof fetchImpl !== 'function') {
    return { sent: 0, pending: queue.length };
  }

  try {
    const response = await fetchImpl(`${apiBase}/api/activity/batch`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ anonymous_id: getAnonymousId(), events: queue }),
    });
    if (!response.ok) throw new Error(`Activity sync failed: ${response.status}`);
    writeJson(ACTIVITY_QUEUE_KEY, []);
    return { sent: queue.length, pending: 0 };
  } catch {
    return { sent: 0, pending: queue.length };
  }
};

const readResumeState = () => {
  const values = readJson(RESUME_STATE_KEY, []);
  return Array.isArray(values) ? values : [];
};

const resumeKey = (item) => `${item?.entity?.type || item?.entity_type || 'content'}:${item?.entity?.id || item?.entity_id || ''}`;
const timestampValue = (value) => {
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const resumeKindForType = (entityType) => {
  const normalized = String(entityType || '').toLowerCase();
  return Object.entries(RESUME_KIND_TYPES).find(([, types]) => types.has(normalized))?.[0] || 'listening';
};

const normalizeRemoteResumeItem = (item = {}) => {
  const metadata = item.metadata || {};
  const metadataEntity = metadata.entity || {};
  const duration = Math.max(0, Number(item.duration_seconds || 0));
  const position = Math.max(0, Number(item.position_seconds || 0));
  return {
    entity: {
      ...metadataEntity,
      id: item.entity_id || metadataEntity.id,
      type: item.entity_type || metadataEntity.type,
      title: metadataEntity.title || metadata.title,
      slug: metadataEntity.slug || metadata.slug,
      media: metadataEntity.media || metadata.media,
    },
    position_seconds: position,
    duration_seconds: duration,
    progress: duration > 0 ? position / duration : 0,
    metadata,
    updated_at: item.updated_at || metadata.updated_at || new Date(0).toISOString(),
  };
};

const isResumable = (item) => item?.entity?.id
  && Number(item.position_seconds || 0) > 10
  && Number(item.progress || 0) < 0.95;

const reconcileResumeStates = (localItems = [], remoteItems = []) => {
  const merged = new Map();
  [...localItems, ...remoteItems.map(normalizeRemoteResumeItem)].forEach((item) => {
    const key = resumeKey(item);
    if (!key || key.endsWith(':')) return;
    const existing = merged.get(key);
    if (!existing || timestampValue(item.updated_at) >= timestampValue(existing.updated_at)) {
      merged.set(key, item);
    }
  });
  return [...merged.values()]
    .filter((item) => item.entity?.id && item.progress < 0.95)
    .sort((a, b) => timestampValue(b.updated_at) - timestampValue(a.updated_at))
    .slice(0, MAX_RESUME_ITEMS);
};

const persistResumeSnapshot = (items) => {
  writeJson(RESUME_STATE_KEY, items);
  try {
    window.dispatchEvent(new CustomEvent(RESUME_STATE_CHANGED_EVENT));
  } catch {
    // Resume continuity must remain safe in non-browser and restricted environments.
  }
  return items;
};

const saveResumeState = (entity, position_seconds, duration_seconds, metadata = {}) => {
  if (!entity?.id || !Number.isFinite(position_seconds)) return readResumeState();
  const progress = duration_seconds > 0 ? position_seconds / duration_seconds : 0;
  const item = {
    entity,
    position_seconds: Math.max(0, position_seconds),
    duration_seconds: Math.max(0, duration_seconds || 0),
    progress,
    metadata,
    updated_at: new Date().toISOString(),
  };
  const next = [item, ...readResumeState().filter((entry) => resumeKey(entry) !== resumeKey(item))]
    .filter((entry) => entry.progress < 0.95)
    .slice(0, MAX_RESUME_ITEMS);
  persistResumeSnapshot(next);
  queueActivity({ activity_type: 'playback_progress', entity, source: 'player', metadata: item });
  return next;
};

const removeResumeState = (entity) => {
  if (!entity?.id) return readResumeState();
  const key = resumeKey({ entity });
  return persistResumeSnapshot(readResumeState().filter((item) => resumeKey(item) !== key));
};

const markResumeCompleted = (entity) => {
  const next = removeResumeState(entity);
  queueActivity({ activity_type: 'completed', entity, source: 'resume-engine' });
  return next;
};

const getContinueByKind = (kind, limit = 8) => readResumeState()
  .filter(isResumable)
  .filter((item) => resumeKindForType(item.entity?.type) === kind)
  .slice(0, limit);

const getResumeRails = (limit = 8) => ([
  { key: 'continue-listening', kind: 'listening', title: 'Continue Listening', items: getContinueByKind('listening', limit) },
  { key: 'continue-watching', kind: 'watching', title: 'Continue Watching', items: getContinueByKind('watching', limit) },
  { key: 'continue-reading', kind: 'reading', title: 'Continue Reading', items: getContinueByKind('reading', limit) },
]).filter((rail) => rail.items.length > 0);

const syncResumeContinuity = async ({ apiBase, token, authenticated = false, fetchImpl = globalThis.fetch } = {}) => {
  if (!apiBase || typeof fetchImpl !== 'function') return { items: readResumeState(), synced: false };
  const anonymousId = getAnonymousId();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  try {
    if (token || authenticated) {
      await fetchImpl(`${apiBase}/api/activity/merge-anonymous?anonymous_id=${encodeURIComponent(anonymousId)}`, {
        method: 'POST', credentials: 'include', headers,
      });
    }
    const response = await fetchImpl(`${apiBase}/api/activity/resume?anonymous_id=${encodeURIComponent(anonymousId)}&limit=${MAX_RESUME_ITEMS}`, { headers, credentials: 'include' });
    if (!response.ok) throw new Error(`Resume hydration failed: ${response.status}`);
    const payload = await response.json();
    const items = reconcileResumeStates(readResumeState(), payload?.items || []);
    persistResumeSnapshot(items);

    await Promise.all(items.map((item) => fetchImpl(`${apiBase}/api/activity/resume?anonymous_id=${encodeURIComponent(anonymousId)}`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        entity_id: item.entity.id,
        entity_type: item.entity.type || 'content',
        position_seconds: item.position_seconds,
        duration_seconds: item.duration_seconds,
        completed: item.progress >= 0.95,
        metadata: { ...item.metadata, entity: item.entity, updated_at: item.updated_at },
      }),
    })));
    return { items, synced: true };
  } catch {
    return { items: readResumeState(), synced: false };
  }
};

const getContinueListening = (limit = 8) => getContinueByKind('listening', limit);
const getContinueWatching = (limit = 8) => getContinueByKind('watching', limit);
const getContinueReading = (limit = 8) => getContinueByKind('reading', limit);

export {
  ACTIVITY_OWNER_KEY,
  ACTIVITY_QUEUE_KEY,
  clearMemberActivity,
  prepareActivityIdentity,
  ANONYMOUS_ID_KEY,
  RESUME_KIND_TYPES,
  RESUME_STATE_KEY,
  RESUME_STATE_CHANGED_EVENT,
  flushActivityQueue,
  getAnonymousId,
  getContinueByKind,
  getContinueListening,
  getContinueReading,
  getContinueWatching,
  getResumeRails,
  markResumeCompleted,
  normalizeRemoteResumeItem,
  queueActivity,
  readActivityQueue,
  readResumeState,
  reconcileResumeStates,
  removeResumeState,
  resumeKindForType,
  saveResumeState,
  syncResumeContinuity,
};
